/**
 * Pure presentation helpers for the Internal Affairs compliance UI: defensive
 * normalization of AI-emitted findings (normalizeGaps precedent) and the
 * status/severity → chip mappings (IncidentAiSummarySection statusChip
 * precedent). Kept free of React so they unit-test without rendering.
 */
import type {
  ComplianceResult,
  ComplianceRun,
  ComplianceRunStatus,
} from "@/features/stationAdmin/api/iaComplianceApi";

export type FindingSeverity = "violation" | "warning" | "info";

export interface NormalizedFinding {
  /** Client-side id for React keys (findings have no server id). */
  id: string;
  severity: FindingSeverity;
  policyReference: string;
  requirement: string;
  evidence: string;
  explanation: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

/**
 * Defensive normalization of model-generated findings, even though the
 * backend sanitizes: filter to records with a requirement, String()-coerce
 * text fields, and whitelist the severity enum. An unknown severity downgrades
 * to "warning" — visible without over-alarming — rather than being dropped.
 */
export function normalizeFindings(raw: unknown): NormalizedFinding[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (item): item is Record<string, unknown> =>
        isRecord(item) && typeof item.requirement === "string",
    )
    .map((item) => ({
      id: crypto.randomUUID(),
      severity:
        (["violation", "warning", "info"] as const).find(
          (v) => v === item.severity,
        ) ?? "warning",
      policyReference: asString(item.policyReference) ?? "",
      requirement: String(item.requirement),
      evidence: asString(item.evidence) ?? "",
      explanation: asString(item.explanation) ?? "",
    }));
}

type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

export const SEVERITY_CHIP_COLOR: Record<FindingSeverity, ChipColor> = {
  violation: "error",
  warning: "warning",
  info: "info",
};

export const SEVERITY_LABEL: Record<FindingSeverity, string> = {
  violation: "Violation",
  warning: "Warning",
  info: "Info",
};

export function runStatusChip(status: ComplianceRunStatus): {
  color: ChipColor;
  label: string;
  inProgress: boolean;
} {
  switch (status) {
    case "queued":
      return { color: "warning", label: "Queued", inProgress: true };
    case "running":
      return { color: "warning", label: "Running", inProgress: true };
    case "completed":
      return { color: "success", label: "Completed", inProgress: false };
    case "failed":
      return { color: "error", label: "Failed", inProgress: false };
  }
}

type CompletedRunCounts = Pick<
  ComplianceRun,
  "reportCount" | "checkedCount" | "violationCount" | "errorCount"
>;

/** Honest completed-run copy for both the detail and compact history views. */
export function completedRunOutcome(run: CompletedRunCounts): {
  detail: string;
  compact: string;
  tone: "error" | "warning" | "success";
} {
  const failed = Math.max(0, run.errorCount);
  const successful = Math.max(0, run.checkedCount - failed);
  const failedReports = `${failed} ${failed === 1 ? "report" : "reports"} could not be checked.`;
  const failedChecks = `${failed} ${failed === 1 ? "check" : "checks"} failed`;

  if (failed > 0) {
    if (run.violationCount > 0) {
      return {
        detail: `${run.violationCount} of ${successful} successful ${successful === 1 ? "check" : "checks"} flagged possible violations; ${failedReports}`,
        compact: `${run.violationCount} flagged · ${failedChecks}`,
        tone: "error",
      };
    }
    return {
      detail: `No violations found in ${successful} successful ${successful === 1 ? "check" : "checks"}; ${failedReports}`,
      compact: failedChecks,
      tone: "warning",
    };
  }

  if (run.violationCount > 0) {
    return {
      detail: `${run.violationCount} of ${run.reportCount} reports flagged for possible violations.`,
      compact: `${run.violationCount} of ${run.reportCount} reports flagged`,
      tone: "error",
    };
  }

  return {
    detail: `All ${run.checkedCount} checked reports look compliant.`,
    compact: "No violations",
    tone: "success",
  };
}

/** "Jul 12, 2026" — doc/report row dates. Empty string for invalid input. */
export function formatShortDate(value: string | Date | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Jul 12, 3:05 PM" — run-history timestamps. Empty string for invalid input. */
export function formatShortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Per-report verdict chip. An errored check is a WARNING (the check failed —
 * nothing is known about compliance), distinct from a red non-compliant
 * verdict; an ok result missing the compliant flag renders neutrally instead
 * of asserting either way.
 */
export function resultVerdictChip(
  result: Pick<ComplianceResult, "status" | "compliant">,
): { color: ChipColor; label: string } {
  if (result.status === "error") {
    return { color: "warning", label: "Check failed" };
  }
  if (result.compliant === false) {
    return { color: "error", label: "Violations found" };
  }
  if (result.compliant === true) {
    return { color: "success", label: "Compliant" };
  }
  return { color: "default", label: "Reviewed" };
}
